import { navSections, type NavGroup, type NavLinkItem } from "../nav/navConfig.js";
import { filterNavItem, type NavVisibilityContext } from "../lib/nav/navVisibility.js";
import { isAdminUser } from "../lib/permissions.js";
import { useMe } from "./useMe.js";
import { useSystemApps } from "./useSystemApps.js";
import { useTvGuideSourceAvailable } from "./useTvGuideSourceAvailable.js";

/**
 * Whether the TV Guide entry is reachable for the current viewer.
 *
 * Decided by the SAME predicate the sidebar applies to the `cc-tv-guide` nav
 * item (`filterNavItem`), so a caller can never point at a TV Guide the nav
 * hides. The entry redeclares every ancestor gate (admin + `wsdashboard`), so
 * running the item filter alone reproduces its effective visibility: admin,
 * WSDashboard enabled with a media server present, and IPTVBoss or Dispatcharr
 * installed for the viewing admin.
 */

/** Locate a nav item by id in the static tree (resolved once at module load). */
const findNavItem = (id: string): NavLinkItem | null => {
  const inItems = (items?: NavLinkItem[]): NavLinkItem | null =>
    items?.find((item) => item.id === id) ?? null;
  const inGroups = (groups?: NavGroup[]): NavLinkItem | null => {
    for (const group of groups ?? []) {
      const hit = inItems(group.items) ?? inGroups(group.groups);
      if (hit) return hit;
    }
    return null;
  };
  for (const section of navSections) {
    const hit = inItems(section.items) ?? inGroups(section.groups);
    if (hit) return hit;
  }
  return null;
};

const TV_GUIDE_NAV_ITEM = findNavItem("cc-tv-guide");
const EMPTY_APP_SET: ReadonlySet<string> = new Set();
const DISPATCHARR_PRESENT_SET: ReadonlySet<string> = new Set(["dispatcharr"]);

export const useTvGuideNavAvailable = (): boolean => {
  const { user, appConfig } = useMe();
  const { installedApps, hasMediaServer } = useSystemApps();
  const isAdmin = isAdminUser(user);
  const wsdashboardEnabled = appConfig?.features?.wsdashboardEnabled ?? false;
  // Box-wide fallback (hook called unconditionally): a Dispatcharr owned by
  // another user is not in this admin's `installedApps`, so consult the guide
  // sources endpoint, gated to the same prerequisites the item declares.
  const guideSourceAvailable = useTvGuideSourceAvailable(
    isAdmin && wsdashboardEnabled && hasMediaServer,
  );
  if (!TV_GUIDE_NAV_ITEM) return false;

  const ctx: NavVisibilityContext = {
    isAdmin,
    user,
    installedApps,
    // `requiresAnyApp` on `installedApps` is the fast path; a box-wide guide
    // source reported by the endpoint counts as present here too.
    configuredRequestProviders: guideSourceAvailable ? DISPATCHARR_PRESENT_SET : EMPTY_APP_SET,
    isFeatureVisible: (feature) =>
      feature === "wsdashboard" ? wsdashboardEnabled && hasMediaServer : !feature,
    passesCapability: () => true,
  };
  return filterNavItem(TV_GUIDE_NAV_ITEM, ctx);
};
